import React, { useState, useMemo, useEffect } from 'react';
import { X, Search, Calendar, MapPin, Clock, Video, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { listContactsCore } from '../lib/contactsCore';
import DatePicker from './DatePicker';
import { createTodo } from '../lib/todos';
import { User } from '@supabase/supabase-js';

interface Contact {
    id: string;
    name: string;
    avatar?: string;
    city?: string;
    state?: string;
    country?: string;
}

interface ContactFirstEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    onEventCreated: (event: any) => void;
    user: User;
    defaultDate?: string;
    defaultTime?: string;
}

export default function ContactFirstEventModal({
    isOpen,
    onClose,
    onEventCreated,
    user,
    defaultDate,
    defaultTime
}: ContactFirstEventModalProps) {
    // Step 1: Who are you meeting?
    const [step, setStep] = useState<'who' | 'what' | 'when' | 'where'>('who');
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

    // Contact search
    const [searchQuery, setSearchQuery] = useState('');
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Event details
    const [eventTitle, setEventTitle] = useState('');
    const [eventDate, setEventDate] = useState(defaultDate || new Date().toISOString().split('T')[0]);
    const [eventTime, setEventTime] = useState(defaultTime || '14:00');
    const [location, setLocation] = useState('');
    const [description, setDescription] = useState('');
    const [includeVideoLink, setIncludeVideoLink] = useState(true);

    // Search contacts
    useEffect(() => {
        if (!searchQuery.trim()) {
            setContacts([]);
            return;
        }

        const searchContacts = async () => {
            setIsSearching(true);
            try {
                const rows = await listContactsCore(supabase as any, { query: searchQuery, limit: 8 });
                const mapped = (rows || []).map((c: any) => ({
                    id: c.id,
                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                    avatar: c.profile_image_url || c.photo || null,
                    city: c.city || null,
                    state: c.state || null,
                    country: c.country || null,
                }));

                setContacts(mapped);
            } catch (error) {
                console.error('Failed to search contacts:', error);
                setContacts([]);
            } finally {
                setIsSearching(false);
            }
        };

        const timeoutId = setTimeout(searchContacts, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Auto-generate event title based on selected contact
    useEffect(() => {
        if (selectedContact && !eventTitle) {
            setEventTitle(`Meeting with ${selectedContact.name}`);
        }
    }, [selectedContact]);

    const handleContactSelect = (contact: Contact) => {
        setSelectedContact(contact);
        setSearchQuery('');
        setContacts([]);
        setStep('what');
    };

    const handleCreateEvent = async () => {
        if (!selectedContact) return;

        try {
            // Combine date and time
            const [hours, minutes] = eventTime.split(':').map(Number);
            const eventDateTime = new Date(eventDate);
            eventDateTime.setHours(hours, minutes, 0, 0);

            // Generate meeting link if requested
            let meetingDescription = description;
            if (includeVideoLink) {
                const meetingId = Math.random().toString(36).substring(2, 15);
                const meetingLink = `https://meet.google.com/${meetingId}`;
                meetingDescription = [
                    description,
                    '',
                    '📹 Meeting Link:',
                    meetingLink,
                    '',
                    '💡 This link was auto-generated. Replace with actual meeting details.'
                ].filter(Boolean).join('\n');
            }

            // Create the calendar event
            const newEvent = await createTodo({
                title: eventTitle,
                description: meetingDescription,
                location: location || null,
                due_date: eventDateTime.toISOString(),
                done: false,
                contact_id: selectedContact.id,
                assigned_contact_id: null,
                group_id: null,
            }, user.id);

            onEventCreated(newEvent);

            // Reset form
            resetForm();
            onClose();
        } catch (error) {
            console.error('Failed to create event:', error);
        }
    };

    const resetForm = () => {
        setStep('who');
        setSelectedContact(null);
        setSearchQuery('');
        setEventTitle('');
        setEventDate(defaultDate || new Date().toISOString().split('T')[0]);
        setEventTime(defaultTime || '14:00');
        setLocation('');
        setDescription('');
        setIncludeVideoLink(true);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />

            <div className="relative w-full max-w-lg mx-4 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                    <h2 className="text-lg font-semibold text-white">
                        {step === 'who' && 'Who are you meeting?'}
                        {step === 'what' && 'What\'s the meeting about?'}
                        {step === 'when' && 'When is the meeting?'}
                        {step === 'where' && 'Where is the meeting?'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4 space-y-4">
                    {/* Step 1: Who */}
                    {step === 'who' && (
                        <div className="space-y-4">
                            {!selectedContact ? (
                                <>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search"
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                            autoFocus
                                        />
                                    </div>

                                    {isSearching && (
                                        <div className="text-sm text-gray-400 text-center py-4">
                                            Searching contacts...
                                        </div>
                                    )}

                                    {contacts.length > 0 && (
                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                            {contacts.map((contact) => (
                                                <button
                                                    key={contact.id}
                                                    onClick={() => handleContactSelect(contact)}
                                                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-700 bg-gray-800 hover:bg-gray-750 text-left"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                                                        {contact.avatar ? (
                                                            <img
                                                                src={contact.avatar}
                                                                alt={contact.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-gray-400 font-medium">
                                                                {contact.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-gray-200 font-medium truncate">
                                                            {contact.name}
                                                        </div>
                                                        <div className="text-sm text-gray-400 truncate">
                                                            {[contact.city, contact.state, contact.country]
                                                                .filter(Boolean)
                                                                .join(', ') || 'No location'}
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {searchQuery && !isSearching && contacts.length === 0 && (
                                        <div className="text-sm text-gray-400 text-center py-4">
                                            No contacts found. Try a different search term.
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 p-4 rounded-lg border border-gray-700 bg-gray-800">
                                        <div className="w-12 h-12 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                                            {selectedContact.avatar ? (
                                                <img
                                                    src={selectedContact.avatar}
                                                    alt={selectedContact.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 font-medium text-lg">
                                                    {selectedContact.name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-gray-200 font-medium">
                                                {selectedContact.name}
                                            </div>
                                            <div className="text-sm text-gray-400">
                                                Selected for meeting
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setSelectedContact(null)}
                                            className="p-2 rounded-md border border-gray-700 text-gray-400 hover:bg-gray-700"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: What */}
                    {step === 'what' && selectedContact && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Meeting Title
                                </label>
                                <input
                                    type="text"
                                    value={eventTitle}
                                    onChange={(e) => setEventTitle(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    placeholder="e.g., Weekly catchup, Project discussion"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Description (optional)
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none"
                                    placeholder="What will you discuss?"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 3: When */}
                    {step === 'when' && selectedContact && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Date
                                </label>
                                <DatePicker
                                    value={eventDate}
                                    onChange={setEventDate}
                                    placeholder="Select date"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Time
                                </label>
                                <input
                                    type="time"
                                    value={eventTime}
                                    onChange={(e) => setEventTime(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 4: Where */}
                    {step === 'where' && selectedContact && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Location (optional)
                                </label>
                                <input
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    placeholder="Office, Coffee shop, or leave blank for video call"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="video-link"
                                    checked={includeVideoLink}
                                    onChange={(e) => setIncludeVideoLink(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="video-link" className="text-sm text-gray-300">
                                    Include video meeting link (Google Meet)
                                </label>
                            </div>

                            {includeVideoLink && (
                                <div className="p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-blue-300">
                                        <Video className="w-4 h-4" />
                                        A Google Meet link will be auto-generated and added to the event description
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                        <div className={`w-2 h-2 rounded-full ${step === 'who' ? 'bg-blue-500' : selectedContact ? 'bg-green-500' : 'bg-gray-600'}`} />
                        <div className={`w-2 h-2 rounded-full ${step === 'what' ? 'bg-blue-500' : eventTitle ? 'bg-green-500' : 'bg-gray-600'}`} />
                        <div className={`w-2 h-2 rounded-full ${step === 'when' ? 'bg-blue-500' : eventDate && eventTime ? 'bg-green-500' : 'bg-gray-600'}`} />
                        <div className={`w-2 h-2 rounded-full ${step === 'where' ? 'bg-blue-500' : 'bg-gray-600'}`} />
                    </div>

                    <div className="flex gap-2">
                        {step !== 'who' && (
                            <button
                                onClick={() => {
                                    const steps = ['who', 'what', 'when', 'where'] as const;
                                    const currentIndex = steps.indexOf(step);
                                    setStep(steps[currentIndex - 1]);
                                }}
                                className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800"
                            >
                                Back
                            </button>
                        )}

                        {step !== 'where' ? (
                            <button
                                onClick={() => {
                                    if (step === 'who' && selectedContact) setStep('what');
                                    else if (step === 'what' && eventTitle) setStep('when');
                                    else if (step === 'when') setStep('where');
                                }}
                                disabled={(step === 'who' && !selectedContact) || (step === 'what' && !eventTitle.trim())}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg"
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                onClick={handleCreateEvent}
                                disabled={!selectedContact || !eventTitle.trim()}
                                className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg"
                            >
                                Create Meeting
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}